import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Button, useTheme, CircularProgress, Alert } from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useQuery } from '@apollo/client';
import { useFormikContext } from 'formik';
import getAdvisorSystemLog from '../../../../features/system-accesses/api/getAdvisorSystemLog';
import DataContext from '../../../../context/DataProvider/DataContext';
import PanelCard from './PanelCard';
import SystemActionDialog from '../SystemActionDialog';
import SystemAccessLogFilterBar from '../../../../features/system-accesses/components/SystemAccessLogFilterBar';
import SystemAccessLogTable from '../../../../features/system-accesses/components/SystemAccessLogTable';
import CancelScheduledActionDialog from '../../../../features/system-accesses/components/CancelScheduledActionDialog';
import EditScheduledActionDialog from '../../../../features/system-accesses/components/EditScheduledActionDialog';
import { isMailDataDirty } from '../../../../features/system-accesses/utils/mailDirty';
import { getPersistedAdvisorId } from '../../../../features/system-accesses/utils/persistedAdvisorId';
import type {
  LogStatusFilter,
  ResendMailSnapshot,
  SystemAccessLogEntry,
} from '../../../../features/system-accesses/types';

const APPLY_COLOR = '#4ADE80';

const EMPTY_IDS: ReadonlySet<number> = new Set<number>();

// Module-level (NOT React state) store for the TERMINAL delivered-but-unrecorded latch:
// entries whose resend mail went out but whose durable record rolled back in the COMMIT
// phase (backend committed=false). The rolled-back row keeps looking resendable, so the
// only thing stopping a duplicate send is this latch — and it must therefore survive the
// panel/AgentEditPage UNMOUNTING on SPA navigation (leave the agent and come back), which
// would reset component `useState` and re-enable the button on an unchanged row (Codex
// Review P1). A module singleton lives in the JS module scope: it survives SPA remounts
// and is cleared only when the JS context is torn down, i.e. a FULL page reload — exactly
// the "cleared by reload, once the operator has reconciled" semantics this state needs.
// Keyed by the globally-unique system_access_log id and carrying the resend-time mail
// header snapshot so the warning can render what actually went out (Codex Review P1).
const deliveredUnrecordedStore = new Map<number, ResendMailSnapshot>();

// CONTRACT — UI pending-latches are PROMISE-owned, never STATUS-FLAG-owned (RITA-1317; full
// rationale + 5-round history in docs/HOTSPOTS.md "SPA-Remount-sichere Latches"). Falsifiable
// rules, each covered by an E2E in system-access-edit-scheduled.spec.ts / system-access-resend.spec.ts:
//  1. A latch is SET the instant an irreversible/lost-update-prone action confirms (resend mail out;
//     edit committed) — before its post-action refetch is awaited.
//  2. A latch is RELEASED ONLY in the `.then()` of the EXACT explicit refetchLog() promise the
//     releasing code itself issued — never derived from networkStatus, notifyOnNetworkStatusChange,
//     a completion re-render, or a bare `logData` reference move. A failed refetch keeps the latch.
//  3. Latches are keyed by (advisorId, logId): reconcile/release act ONLY on the entries of the
//     advisor whose refetch actually ran. A fetch for advisor B never releases advisor A's latch.
//  4. Latches live in module scope (survive an SPA panel/AgentEditPage remount) and are re-seeded
//     into component state per current advisor; only a full page reload clears them.
//  5. Remount reconcile = re-issue an awaited refetch for the restored keys (idempotent); on reject
//     the latch stays and the row keeps its pending affordance — never silently unlock, never hang
//     without a retry path (a later successful refetch reconciles).
//  6. The TERMINAL committed=false latch (deliveredUnrecordedStore) is NEVER released by a refetch —
//     the rolled-back row still looks resendable; only a full reload clears it.
//
// Advisor-scoped, module-level latch store that survives an SPA remount of the
// SystemAccessPanel/AgentEditPage (navigate away and back). Two latch classes need exactly this
// remount-safety — the POST-EDIT row lock (lost-update guard) and the RESEND-SUCCESS button lock
// (duplicate-send guard) — so both back onto this same store type (Codex Review P1, class
// `spa-remount-safety-latches`). Keyed by ADVISOR id, not just the globally-unique log id: a fresh
// GetAdvisorSystemLog for advisor B does NOT prove advisor A's latched row was refreshed, so
// releasing A's latch on B's fetch would reopen the very window the latch closes (Codex Review P1,
// cross-advisor scope). Each advisor keeps its own id set; a remount on a different advisor neither
// sees nor reconciles another advisor's latch. Lives in JS module scope → survives SPA remounts,
// cleared only on a full page reload.
class RemountSafeLatchStore {
  private byAdvisor = new Map<number, Set<number>>();

  // Defensive copy so a caller can never mutate the stored set in place.
  snapshot(advisorId: number): ReadonlySet<number> {
    const ids = this.byAdvisor.get(advisorId);
    return ids && ids.size > 0 ? new Set(ids) : EMPTY_IDS;
  }

  replace(advisorId: number, ids: ReadonlySet<number>): void {
    if (!advisorId) return;
    if (ids.size === 0) this.byAdvisor.delete(advisorId);
    else this.byAdvisor.set(advisorId, new Set(ids));
  }
}

// POST-EDIT row lock (lost-update safety): until a refetch that started AFTER the edit lands, the
// table still holds PRE-edit content/date, and re-opening the dialog on those stale values lets a
// follow-up save overwrite the edit that already committed on the server. `cache-and-network`
// re-serves the cached PRE-edit rows on a remount BEFORE the fresh response lands, so the lock must
// outlive component state — hence the advisor-scoped module store; it is reconciled (released) once
// a fresh network fetch for that advisor lands (see the reconcile effect below, Codex Review P1).
const pendingEditStore = new RemountSafeLatchStore();

// RESEND-SUCCESS button lock (duplicate-send safety): a confirmed resend whose post-resend refetch
// has not landed yet still renders the row as resendable from cache, so the lock keeps the button
// replaced until a fresh fetch reconciles it. Same remount-safety class as the edit lock — without
// a module-backed store a successful resend whose refetch failed would lose the lock on
// navigate-away-and-back and re-open the duplicate-send window (Codex Review P1).
const pendingResendStore = new RemountSafeLatchStore();

// Drop every id that was latched BEFORE this refetch started; ids latched WHILE the refetch
// was in flight cannot be reflected in its response yet, so they stay. Shared by the resend
// latch and the post-edit row lock so both release on exactly the refetch that can reflect
// them (Codex Review P1 / Audit P2).
function releaseLatched(prev: ReadonlySet<number>, snapshotBeforeRefetch: ReadonlySet<number>): ReadonlySet<number> {
  if (prev.size === 0) return prev;
  const next = new Set<number>();
  prev.forEach((id) => {
    if (!snapshotBeforeRefetch.has(id)) next.add(id);
  });
  return next.size === prev.size ? prev : next;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const input = /^\d+$/.test(dateStr) ? Number(dateStr) : dateStr;
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

interface AdvisorFormValues {
  agent_id: number;
  status_id: number;
  dismissed_at?: string | null;
  name: string;
  surname: string;
  address: string;
  zip: string;
  city: string;
  country_id: number;
  branch_office_id: number;
  country?: { id: number; title: string } | null;
  branch_office?: { id: number; title: string } | null;
  email_corp: string;
  email_private: string;
  business_owner: string;
  phone: string | null;
  mobile: string | null;
  fax: string | null;
  birthday: string;
  is_male: number;
  rank_id: number;
  level_id: number | null;
  contract_type_id: number | null;
  address2: string | null;
  zip2: string | null;
  city2: string | null;
  country2_id: number | null;
  iban: string | null;
  bic: string | null;
  bank_name: string | null;
  tax_nr: string | null;
  start_date: string | null;
  agent_account_nr: number;
  myjack_login: string | null;
  mandateReference: string;
}

interface SystemAccessPanelProps {
  agentId?: string;
}

function SystemAccessPanel({ agentId }: SystemAccessPanelProps) {
  const theme = useTheme();
  const { country: countries, brachOffices, status, rank, level, contractType } = useContext(DataContext);
  const { values, initialValues } = useFormikContext<AdvisorFormValues>();
  // Backend lookups (system log, provider list, template preview) and the send
  // mutation key off the PERSISTED advisor number, not the live form value. A
  // dirty/unsaved agent_id would otherwise make the backend (which validates
  // advisor_id) fail with ADVISOR_NOT_FOUND and break the preview/log. The live
  // `values` still feed the body placeholders; sending stays blocked while the
  // form is mail-dirty (RITA-1307 Review).
  const advisorId = getPersistedAdvisorId(initialValues);

  // Block sending only when a field that actually feeds the generated mail is
  // unsaved — not on Formik's global `dirty`, which would also fire for
  // unrelated edits (dismissal status, another tab) and needlessly disable the
  // system-access actions (RITA-1307 Review P2).
  const mailDataDirty = useMemo(() => isMailDataDirty(values, initialValues), [values, initialValues]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState<LogStatusFilter>('all');
  const [cancelTarget, setCancelTarget] = useState<SystemAccessLogEntry | null>(null);
  // The still-scheduled row currently being edited (RITA-1317). Held here (not in the
  // detail row) so the dialog survives the row collapsing/paging/filtering, mirroring
  // cancelTarget.
  const [editTarget, setEditTarget] = useState<SystemAccessLogEntry | null>(null);
  // Live mirror of the persisted advisor id for the mutation callbacks below: they write the
  // advisor-scoped module store and must always target the CURRENT advisor without re-creating
  // their identity on every advisor change.
  const advisorIdRef = useRef(advisorId);
  advisorIdRef.current = advisorId;

  // Entries whose confirmed resend is still awaiting a durable log refresh. Held here (not in the
  // detail row) so the latch survives the row collapsing/paging/filtering under `Collapse
  // unmountOnExit`, and released once a log refresh that STARTED AFTER the resend lands. Seeded
  // from the advisor-scoped module store so it ALSO survives a panel/AgentEditPage remount on SPA
  // navigation: a confirmed resend whose post-resend refetch failed must keep the button replaced
  // until a fresh fetch reconciles it, else navigate-away-and-back reopens the duplicate-resend
  // window (Codex Review P1, class spa-remount-safety-latches).
  const [resentPendingIds, setResentPendingIds] = useState<ReadonlySet<number>>(() =>
    pendingResendStore.snapshot(advisorId),
  );
  // Entries whose mail went out but whose durable record rolled back in the COMMIT phase
  // (backend committed=false). Unlike `resentPendingIds` this latch is TERMINAL: it is NEVER
  // released by a log refresh, because the rolled-back row keeps looking resendable, so
  // releasing would re-enable the button on an unchanged row and invite a duplicate send.
  // Backed by the module-level `deliveredUnrecordedStore` (initialized from it here) so the
  // latch — and the resend-time mail header snapshot it carries — survives BOTH the detail
  // unmounting under Collapse `unmountOnExit` AND the whole panel/AgentEditPage unmounting on
  // SPA navigation; it is cleared only by a full reload (Codex Review P1).
  const [resendUnrecorded, setResendUnrecorded] = useState<ReadonlyMap<number, ResendMailSnapshot>>(
    () => new Map(deliveredUnrecordedStore),
  );
  // Synchronous mirror of the latch set. State updaters run during React's render phase, not at
  // call time, so they CANNOT give refreshLog a reliable "latches as of right now" snapshot. The
  // ref is updated synchronously alongside every state change, so refreshLog can read the exact
  // pre-refetch latch set the instant it starts (Codex Review P1 follow-up).
  const resentPendingIdsRef = useRef<ReadonlySet<number>>(resentPendingIds);
  // Entries whose edit was saved but whose post-edit log refresh has not landed yet. Until it
  // does, the table still shows the PRE-edit content/date, so re-opening the dialog would edit
  // STALE data; this latch keeps the row's edit/cancel affordances locked through the refresh.
  // Held here (not in the detail) so it survives the row collapsing/paging/filtering under
  // Collapse `unmountOnExit`, mirroring the resend latch. Seeded from the advisor-scoped module
  // store so the lock ALSO survives a panel/AgentEditPage remount on SPA navigation — without that,
  // a failed post-edit refetch followed by navigate-away-and-back would re-enable `Bearbeiten` on
  // the cache-and-network PRE-edit rows and reopen the lost-update window (Codex Review P1).
  const [editPendingIds, setEditPendingIds] = useState<ReadonlySet<number>>(() => pendingEditStore.snapshot(advisorId));
  // Synchronous mirror of the edit latch, same rationale as resentPendingIdsRef: refreshLog must
  // read the exact pre-refetch set the instant it starts. Seeded from the same store as the state.
  const editPendingIdsRef = useRef<ReadonlySet<number>>(editPendingIds);
  // Snapshots of the latches RESTORED from the store for the CURRENT advisor, captured at mount
  // (and re-captured if the advisor changes — see the reconcile effect). The reconcile effect
  // releases exactly these once a fresh fetch lands — never a latch added during THIS mount (those
  // release through refreshLog's own pre-refetch snapshot).
  const restoredEditLatchRef = useRef<ReadonlySet<number>>(editPendingIds);
  const restoredResendLatchRef = useRef<ReadonlySet<number>>(resentPendingIds);
  // Which advisor the in-component latch state currently mirrors. The reconcile effect re-seeds
  // when this diverges from `advisorId` (late-loading initialValues, or an in-place route param
  // change that does NOT remount the panel) so component state always holds ONLY the current
  // advisor's latches (Codex Review P1, cross-advisor scope).
  const seededAdvisorRef = useRef(advisorId);
  // The advisor whose RESTORED latches a fresh fetch already released on this mount, plus an
  // in-flight guard so a re-rendered effect never fires duplicate reconcile refetches.
  const reconciledAdvisorRef = useRef<number | null>(null);
  const reconcileInFlightRef = useRef(false);
  // Distinguishes a deps-change effect re-run (keep an in-flight reconcile) from an unmount (drop
  // its result), so a reconcile refetch resolving after unmount never calls setState.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Shared mutation core for both advisor-scoped latches. The latch MUST be written under the
  // advisor that ORIGINATED the action, not whichever advisor happens to be on screen when a late
  // async callback (post-resend / post-edit, or a refetch that resolves after a switch) finally
  // runs. After a client-side advisor switch `advisorIdRef.current` already points at the new
  // advisor, so reading it at completion time would file advisor A's latch under advisor B —
  // dropping A's duplicate-send / lost-update guard and misapplying it to B (Codex Review P1, class
  // originating-advisor-latch-binding). Callers therefore pass the originating advisor explicitly:
  //   - originating advisor === the advisor currently displayed → keep the synchronous ref, the
  //     render state and the module store in lock-step (the on-screen path).
  //   - originating advisor != the displayed one (the action finished after a switch) → persist ONLY
  //     into that advisor's module-store slot so returning to it restores the latch; never touch the
  //     current advisor's ref/state, which mirror a DIFFERENT advisor.
  const applyLatchMutation = useCallback(
    (
      originatingAdvisorId: number,
      store: RemountSafeLatchStore,
      ref: React.MutableRefObject<ReadonlySet<number>>,
      setState: React.Dispatch<React.SetStateAction<ReadonlySet<number>>>,
      mutator: (prev: ReadonlySet<number>) => ReadonlySet<number>,
    ) => {
      if (!originatingAdvisorId) return;
      if (originatingAdvisorId === advisorIdRef.current) {
        const next = mutator(ref.current);
        if (next === ref.current) return;
        // A ref's `.current` is mutable by contract — assigning the synchronous mirror here is the
        // whole point of passing it in (keeps the off-screen vs on-screen branching in one place).
        // eslint-disable-next-line no-param-reassign
        ref.current = next;
        store.replace(originatingAdvisorId, next);
        setState(next);
        return;
      }
      const prev = store.snapshot(originatingAdvisorId);
      const next = mutator(prev);
      if (next === prev) return;
      store.replace(originatingAdvisorId, next);
    },
    [],
  );

  // Single mutation point for the resend latch (duplicate-send guard), bound to the originating
  // advisor so a late post-resend callback after an advisor switch still files it correctly.
  const mutateResentPending = useCallback(
    (originatingAdvisorId: number, mutator: (prev: ReadonlySet<number>) => ReadonlySet<number>) => {
      applyLatchMutation(originatingAdvisorId, pendingResendStore, resentPendingIdsRef, setResentPendingIds, mutator);
    },
    [applyLatchMutation],
  );

  // Single mutation point for the edit latch (lost-update guard), bound to the originating advisor
  // exactly like the resend latch.
  const mutateEditPending = useCallback(
    (originatingAdvisorId: number, mutator: (prev: ReadonlySet<number>) => ReadonlySet<number>) => {
      applyLatchMutation(originatingAdvisorId, pendingEditStore, editPendingIdsRef, setEditPendingIds, mutator);
    },
    [applyLatchMutation],
  );

  const {
    data: logData,
    loading: logLoading,
    refetch: refetchLog,
    error: logError,
  } = useQuery(getAdvisorSystemLog, {
    variables: { advisor_id: advisorId },
    skip: !advisorId || advisorId === 0,
    fetchPolicy: 'cache-and-network',
    // Opt this query out of Apollo's query deduplication. The resend latch is released when a
    // refreshLog() refetch that STARTED AFTER the resend lands. With dedup on, a resend's own
    // refetch would be merged into an older in-flight log request whose response predates the
    // resend — releasing the latch on stale data and reopening the duplicate-resend window. Log
    // refetches are user-action-triggered and infrequent, so opting out costs nothing and keeps
    // every refresh a genuine, current request (Codex Review P1 follow-up).
    context: { queryDeduplication: false },
  });

  const logEntries: SystemAccessLogEntry[] = useMemo(() => logData?.getAdvisorSystemLog || [], [logData]);

  // Synchronous mirror of the latest query data so the reconcile effect's async callbacks can tell
  // whether a FRESH network result landed for this advisor during their in-flight window — used to
  // recover from a failed reconcile refetch without stranding a restored latch (Codex Review P1).
  const logDataRef = useRef(logData);
  logDataRef.current = logData;

  // Single refresh path for the whole panel: refetch the log and, ONLY on success, drop the
  // resend latches that this refetch can actually reflect. A latch bridges the gap between a
  // confirmed resend and the durable refresh that reflects it. We snapshot the latches that
  // already exist BEFORE kicking off the refetch and release only those on completion — a
  // resend latched WHILE this refetch is in flight cannot be in its response yet, so an older
  // refresh (a cancel/create via handle*Complete, or an earlier overlapping resend) must NOT
  // clear it, otherwise the newer row turns resendable again on stale data and the
  // duplicate-resend window reopens. Releasing on refetch COMPLETION (not on the data object
  // changing) means an identical snapshot still clears the in-scope latches, and a transient
  // refetch failure can't strand a row as permanently non-resendable — the next refresh that
  // started after the resend reconciles it (Codex Review P1).
  const refreshLog = useCallback(async () => {
    // Synchronously snapshot the latches that already exist BEFORE kicking off the refetch
    // (handleResendSuccess / handleEditSuccess latch into the ref, then call refreshLog). Only
    // these can be reflected once this refetch lands; anything latched while it is in flight is
    // left intact and released by the next refresh that started after it.
    const resendLatchedBeforeRefetch = resentPendingIdsRef.current;
    const editLatchedBeforeRefetch = editPendingIdsRef.current;
    // Bind the release to the advisor whose latches we just snapshotted. If this refetch resolves
    // AFTER a client-side advisor switch, releasing against the (now different) displayed advisor
    // would clear the wrong store slot; capturing it here keeps the release on the originating
    // advisor (Codex Review P1, originating-advisor-latch-binding).
    const advisorAtRefetch = advisorIdRef.current;
    // Let a failing refetch PROPAGATE without releasing either latch (no try/finally). Both
    // latches must only release on a SUCCESSFUL refetch — see the per-latch rationale below.
    const result = await refetchLog();
    // The resend latch is a SAFETY mechanism: releasing it re-enables the resend button, so it
    // must release ONLY on a successful refetch (on failure the row still looks resendable and a
    // later refresh that started after the resend reconciles it — releasing on failure would
    // reopen the duplicate-send window).
    if (resendLatchedBeforeRefetch.size > 0) {
      mutateResentPending(advisorAtRefetch, (prev) => releaseLatched(prev, resendLatchedBeforeRefetch));
    }
    // The edit latch is ALSO a safety mechanism, not just a UX guard: until a refetch that
    // started AFTER the edit lands, the table still holds the PRE-edit mail_content/scheduled_at.
    // If a post-edit refetch FAILS and we released the latch anyway, the row would become editable
    // on stale data — reopening the dialog then seeds it from the pre-edit values and a follow-up
    // save (e.g. changing only the date) ships the STALE body back, OVERWRITING the edit that
    // already committed on the server. That is a lost update / customer-visible data corruption,
    // not a transient UX glitch (Codex Review P1). So release it ONLY on success, exactly like the
    // resend latch: a later refresh that started after the edit (any subsequent panel action) or a
    // remount with a fresh fetch reconciles the row; the worst case is the row stays locked in
    // "Wird aktualisiert…" until then — strictly safer than a stale-data overwrite.
    if (editLatchedBeforeRefetch.size > 0) {
      mutateEditPending(advisorAtRefetch, (prev) => releaseLatched(prev, editLatchedBeforeRefetch));
    }
    return result;
  }, [refetchLog, mutateResentPending, mutateEditPending]);

  // Fire-and-forget refresh for the create/cancel/edit COMPLETION handlers. refreshLog
  // intentionally REJECTS on a failed refetch so the awaiting resend/edit-latch paths can keep
  // their latch (release only on success). These event-handler callers have no caller to
  // propagate to, so they must absorb that rejection HERE — otherwise a transient
  // GetAdvisorSystemLog failure leaks as an unhandled promise rejection. Swallowing is safe: the
  // mutation already committed server-side, the edit latch (when set) stays locked on failure, and
  // the next user-triggered read reconciles the table (Codex Review C, refreshlog-error-propagation
  // class — same explicit handling the resend path already has).
  const refreshLogSafe = useCallback(() => {
    refreshLog().catch(() => {});
  }, [refreshLog]);

  // Reconcile the edit AND resend latches RESTORED from the advisor-scoped module store on a
  // panel/AgentEditPage remount. cache-and-network re-serves the cached PRE-edit/PRE-resend rows
  // first, so a restored latch must stay locked until a FRESH network read of THIS advisor's log
  // actually lands. We do NOT key this off Apollo's `networkStatus`, because that signal is
  // unreliable for both edges of this case:
  //   (a) When the query is SKIPPED (no persisted advisor — e.g. the "new agent" route, or while
  //       initialValues are still loading) Apollo reports `ready` WITHOUT ever fetching. Releasing
  //       there would drop the lock without a single server read (Codex Review P1).
  //   (b) With `notifyOnNetworkStatusChange: false` (the default) Apollo may NOT re-render the
  //       loading→ready transition when the network response is identical to the cache (Codex
  //       Review P2).
  // So we drive reconciliation off an EXPLICIT refetch promise — the same discipline as refreshLog:
  // release the restored snapshot ONLY when a fresh fetch resolves SUCCESSFULLY. We release ONLY the
  // mount snapshot, never a latch added during this mount (those go through refreshLog's own
  // pre-refetch snapshot).
  useEffect(() => {
    const currentAdvisor = advisorId;

    // The persisted advisor changed since we last seeded (late-loading initialValues, or an
    // in-place route param change that keeps the panel mounted). Re-seed component state from the
    // store so it holds ONLY this advisor's latches; the previous advisor's latches stay safe in
    // the store under its own key and are never released by THIS advisor's fetch (Codex Review P1,
    // cross-advisor scope).
    if (seededAdvisorRef.current !== currentAdvisor) {
      seededAdvisorRef.current = currentAdvisor;
      const seededEdit = pendingEditStore.snapshot(currentAdvisor);
      const seededResend = pendingResendStore.snapshot(currentAdvisor);
      editPendingIdsRef.current = seededEdit;
      resentPendingIdsRef.current = seededResend;
      restoredEditLatchRef.current = seededEdit;
      restoredResendLatchRef.current = seededResend;
      reconciledAdvisorRef.current = null;
      reconcileInFlightRef.current = false;
      setEditPendingIds(seededEdit);
      setResentPendingIds(seededResend);

      // A client-side advisor switch keeps THIS panel mounted (the AgentEditPage route param
      // changes), so a per-row/compose dialog opened for the PREVIOUS advisor would otherwise
      // survive the switch. The edit and cancel dialogs hold the previous advisor's row in
      // `editTarget`/`cancelTarget` (frozen logId), so a save from them still mutates advisor A's
      // scheduled row while the operator is already on advisor B's page — a wrong-row write /
      // customer-visible data corruption, not just a UX glitch (Codex Review P1). The compose
      // dialog binds to the live `advisorId` but could likewise carry half-typed content across the
      // switch. Close every transient dialog on the advisor change so a stale modal can never act on
      // the previous advisor. Setting an already-closed dialog to its current value is a no-op.
      setEditTarget(null);
      setCancelTarget(null);
      setDialogOpen(false);
    }

    // No persisted advisor → the log query is skipped: nothing to reconcile against and no fetch to
    // await. KEEP the locks so a later mount/advisor with a genuine server read reconciles them.
    if (!currentAdvisor || currentAdvisor === 0) return undefined;
    // This advisor's restored latches were already reconciled by a successful fetch on this mount.
    if (reconciledAdvisorRef.current === currentAdvisor) return undefined;

    const restoredEdit = restoredEditLatchRef.current;
    const restoredResend = restoredResendLatchRef.current;
    if (restoredEdit.size === 0 && restoredResend.size === 0) {
      reconciledAdvisorRef.current = currentAdvisor;
      return undefined;
    }
    // A reconcile refetch is already pending → don't fire a duplicate (this effect re-runs on
    // logData changes; see deps).
    if (reconcileInFlightRef.current) return undefined;

    // Release the restored snapshot for THIS advisor exactly once. Guarded against unmount and a
    // mid-flight advisor change so we never release another advisor's latch or setState after
    // unmount. Releases are bound to `currentAdvisor` (originating-advisor-latch-binding).
    const releaseRestored = () => {
      if (!mountedRef.current) return;
      if (advisorIdRef.current !== currentAdvisor) return;
      if (reconciledAdvisorRef.current === currentAdvisor) return;
      reconciledAdvisorRef.current = currentAdvisor;
      if (restoredEdit.size > 0) mutateEditPending(currentAdvisor, (prev) => releaseLatched(prev, restoredEdit));
      if (restoredResend.size > 0) mutateResentPending(currentAdvisor, (prev) => releaseLatched(prev, restoredResend));
    };

    // Reconcile by firing an EXPLICIT refetch and releasing the restored latch ONLY when that
    // refetch resolves SUCCESSFULLY — the single reliable proof a fresh network read of THIS
    // advisor's log landed. A `logData` reference change is NOT such proof: under
    // `cache-and-network` Apollo also swaps in CACHED rows (a variables change, an unrelated cache
    // write) before any fresh response arrives. Releasing on a cache-only `logData` move while the
    // explicit refetch FAILED would clear the lock on stale PRE-edit/PRE-resend rows and reopen the
    // duplicate-send / lost-update window this latch closes (Codex Review P1).
    const attemptReconcile = () => {
      // Snapshot `logData` as THIS refetch starts — used ONLY to decide whether to RETRY, never to
      // release. If the refetch fails but the concurrent cache-and-network mount query moved
      // `logData` during our in-flight window, the effect won't re-run for that already-consumed
      // change; we then re-arm ONE fresh explicit refetch and again gate the release on ITS
      // success. A persistent backend error produces no further `logData` movement → no retry → no
      // busy-loop (Codex Review P1, retry-after-failed-refetch).
      const logDataAtFire = logDataRef.current;
      refetchLog()
        .then(() => {
          // Release ONLY after a fresh fetch actually SUCCEEDS. Setting the reconciled flag before
          // the await was the old bug: a failed explicit refetch alongside a successful mount query
          // discarded the success and stranded the row in the pending state until a manual refresh
          // (Codex Review P1).
          releaseRestored();
        })
        .catch(() => {
          // Fresh fetch failed → keep the locks; the in-flight-window retry below recovers if the
          // mount query meanwhile landed fresh data, otherwise a later successful log landing
          // re-runs this effect (logData dep), so a transient error can't strand the row.
        })
        .finally(() => {
          reconcileInFlightRef.current = false;
          // Already reconciled (the .then released) → nothing to retry.
          if (reconciledAdvisorRef.current === currentAdvisor) return;
          if (!mountedRef.current) return;
          if (advisorIdRef.current !== currentAdvisor) return;
          // `logData` moved while we were in flight (a fresh mount-query landing the effect already
          // consumed). We CANNOT tell a cache serve from a network landing by the reference alone,
          // so we do NOT release here — we re-arm one fresh explicit refetch and let ITS success
          // gate the release. Each attempt re-captures `logDataAtFire`, so once `logData` stops
          // moving there is no further retry (bounded by real data events, never a busy-loop).
          if (logDataRef.current !== logDataAtFire) {
            reconcileInFlightRef.current = true;
            attemptReconcile();
          }
        });
    };

    reconcileInFlightRef.current = true;
    attemptReconcile();
    return undefined;
  }, [advisorId, refetchLog, mutateEditPending, mutateResentPending, logData]);

  const isDismissed = values.status_id === 5;

  const counts = useMemo(() => {
    const acc: Record<LogStatusFilter, number> = {
      all: logEntries.length,
      applied: 0,
      planned: 0,
      not_sent: 0,
      cancelled: 0,
    };
    logEntries.forEach((entry) => {
      if (entry.process_status && acc[entry.process_status] !== undefined) {
        acc[entry.process_status] += 1;
      }
    });
    return acc;
  }, [logEntries]);

  const filteredEntries = useMemo(() => {
    if (filter === 'all') return logEntries;
    return logEntries.filter((entry) => entry.process_status === filter);
  }, [filter, logEntries]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleActionComplete = () => {
    setDialogOpen(false);
    refreshLogSafe();
  };

  const handleCancelRequest = (entry: SystemAccessLogEntry) => {
    setCancelTarget(entry);
  };

  const handleCancelClose = () => {
    setCancelTarget(null);
  };

  const handleCancelSuccess = () => {
    setCancelTarget(null);
    refreshLogSafe();
  };

  const handleEditRequest = (entry: SystemAccessLogEntry) => {
    setEditTarget(entry);
  };

  const handleEditClose = () => {
    setEditTarget(null);
  };

  const handleEditSuccess = () => {
    // Capture the edited row id BEFORE clearing the target, then lock it until the post-edit
    // refresh lands. Until the refetch reflects the new content/date the table still shows the
    // PRE-edit version, so an immediate re-edit would start from stale data. Latching first and
    // then awaiting refreshLog() puts this id into refreshLog's pre-refetch snapshot, so the
    // edit's OWN refresh releases it once landed; an older in-flight refresh leaves it intact
    // (mirrors the resend latch, Audit/Review P2).
    const editedId = editTarget?.id ?? null;
    // Bind the lock to the advisor that OWNS the edited row, not whichever advisor is displayed when
    // this completion runs — a client-side switch between save and completion must not file the lock
    // under the wrong advisor (Codex Review P1, originating-advisor-latch-binding).
    const originatingAdvisor = editTarget?.advisor_id ?? advisorIdRef.current;
    setEditTarget(null);
    if (editedId !== null) {
      mutateEditPending(originatingAdvisor, (prev) => {
        if (prev.has(editedId)) return prev;
        const next = new Set(prev);
        next.add(editedId);
        return next;
      });
    }
    refreshLogSafe();
  };

  // Latch the entry as resent BEFORE awaiting the refresh: the mail is already out, so
  // the button must stay replaced even while the refresh is in flight or fails. The
  // latch lives here (keyed by id) so it survives the detail unmounting. We latch first and
  // then await refreshLog(): that ordering puts this id into refreshLog's pre-refetch
  // snapshot, so the resend's OWN refresh releases it once landed, while an older in-flight
  // refresh (which snapshotted before this latch) leaves it intact. Even if THIS refetch
  // rejects, a later refresh that started after this latch still clears it, so the row never
  // stays permanently non-resendable (Codex Review P1). We await it and let it throw on
  // failure, so the detail keeps its in-mount confirmation up until a durable refresh arrives.
  const handleResendSuccess = async (entryId: number, originatingAdvisorId: number) => {
    // `originatingAdvisorId` is the resent row's own advisor_id (immutable, threaded up from the
    // detail), so even if the operator switches advisor before this callback runs the latch is filed
    // under the advisor that actually resent — not the one now on screen (Codex Review P1,
    // originating-advisor-latch-binding).
    mutateResentPending(originatingAdvisorId, (prev) => {
      if (prev.has(entryId)) return prev;
      const next = new Set(prev);
      next.add(entryId);
      return next;
    });
    await refreshLog();
  };

  // The resend's mail went out but its durable record rolled back (backend committed=false).
  // Latch the entry TERMINALLY (with the resend-time mail header snapshot) so the button stays
  // replaced by a warning that shows what actually went out — even across detail unmounts AND a
  // panel/AgentEditPage remount, because we persist into the module-level store first. We
  // deliberately do NOT refreshLog()/release: the rolled-back row is unchanged and would turn
  // resendable again, inviting a duplicate send. Only a full reload clears it, by which point the
  // operator has reconciled the delivered-but-unrecorded mail (Codex Review P1).
  const handleResendUnrecorded = useCallback((entryId: number, snapshot: ResendMailSnapshot) => {
    if (deliveredUnrecordedStore.has(entryId)) return;
    deliveredUnrecordedStore.set(entryId, snapshot);
    setResendUnrecorded(new Map(deliveredUnrecordedStore));
  }, []);

  if (!agentId) {
    return (
      <Box>
        <Typography sx={{ fontSize: 17, fontWeight: 800, mb: 0.5 }}>Systemzugänge</Typography>
        <Typography sx={{ fontSize: 13, color: theme.palette.text.disabled, mb: 3 }}>
          Zugänge bei externen Dienstleistern für diesen Berater beantragen
        </Typography>
        <Typography sx={{ color: theme.palette.text.disabled, fontSize: 14 }}>
          Bitte speichere den Berater zuerst, um Systemzugänge zu verwalten.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header without card frame — title left, primary action right */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, mb: 0.5 }}>Systemzugänge</Typography>
          <Typography sx={{ fontSize: 13, color: theme.palette.text.disabled }}>
            Zugänge bei externen Dienstleistern für diesen Berater beantragen.
          </Typography>
        </Box>
        <Button
          onClick={handleOpenDialog}
          startIcon={<AddCircleOutlineIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 13,
            px: 2.25,
            py: 1,
            borderRadius: 1.5,
            color: APPLY_COLOR,
            bgcolor: alpha(APPLY_COLOR, 0.12),
            border: `1px solid ${alpha(APPLY_COLOR, 0.35)}`,
            whiteSpace: 'nowrap',
            alignSelf: { xs: 'stretch', sm: 'auto' },
            '&:hover': {
              bgcolor: alpha(APPLY_COLOR, 0.2),
            },
          }}
        >
          Beantragen
        </Button>
      </Box>

      {isDismissed && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{
            mb: 3,
            bgcolor: alpha('#F59E0B', 0.08),
            border: `1px solid ${alpha('#F59E0B', 0.3)}`,
            borderLeft: `4px solid #F59E0B`,
            '& .MuiAlert-icon': { color: '#F59E0B' },
            '& .MuiAlert-message': { color: theme.palette.text.primary },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Berater ist gekündigt</Typography>
          <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary, mt: 0.5 }}>
            Dieser Berater wurde{values.dismissed_at ? ` am ${formatDate(values.dismissed_at)}` : ''} gekündigt. Bitte
            prüfen, ob alle Systemzugänge ebenfalls gekündigt wurden. Aktionen können weiterhin ausgeführt werden.
          </Typography>
        </Alert>
      )}

      {/* Action Log */}
      <PanelCard title="" subtitle="">
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: theme.palette.text.disabled,
            mb: 2,
          }}
        >
          AKTIONSPROTOKOLL
        </Typography>

        {logLoading && logEntries.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {/* CONTRACT: query error (e.g. schema mismatch on partial rollout) must NOT
            collapse into a false "no data" empty state — show an explicit warning instead. */}
        {!logLoading && logError && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Aktionen konnten nicht geladen werden.
          </Alert>
        )}
        {!logLoading && !logError && logEntries.length === 0 && (
          <Typography sx={{ color: theme.palette.text.disabled, fontSize: 13 }}>
            Noch keine Aktionen für diesen Berater.
          </Typography>
        )}
        {logEntries.length > 0 && (
          <>
            <SystemAccessLogFilterBar value={filter} counts={counts} onChange={setFilter} />
            <SystemAccessLogTable
              entries={filteredEntries}
              onCancel={handleCancelRequest}
              onEdit={handleEditRequest}
              editPendingIds={editPendingIds}
              resentPendingIds={resentPendingIds}
              onResendSuccess={handleResendSuccess}
              resendUnrecordedSnapshots={resendUnrecorded}
              onResendUnrecorded={handleResendUnrecorded}
            />
          </>
        )}
      </PanelCard>

      {/* Action Dialog */}
      <SystemActionDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onComplete={handleActionComplete}
        advisorId={advisorId}
        advisorValues={values}
        countries={countries}
        branchOffices={brachOffices}
        formDirty={mailDataDirty}
        statuses={status}
        ranks={rank}
        levels={level}
        contractTypes={contractType}
      />

      <CancelScheduledActionDialog
        open={cancelTarget !== null}
        logId={cancelTarget?.id ?? null}
        providerName={cancelTarget?.provider_name ?? null}
        scheduledAt={cancelTarget?.scheduled_at ?? null}
        onClose={handleCancelClose}
        onSuccess={handleCancelSuccess}
      />

      <EditScheduledActionDialog
        open={editTarget !== null}
        logId={editTarget?.id ?? null}
        providerName={editTarget?.provider_name ?? null}
        scheduledAt={editTarget?.scheduled_at ?? null}
        mailContent={editTarget?.mail_content ?? null}
        onClose={handleEditClose}
        onSuccess={handleEditSuccess}
      />
    </Box>
  );
}

export default SystemAccessPanel;
